/* quiz.js — Pique Vitality product-finder.

   Two engines, chosen by the section's data-mode:

   • OUTCOME ("outcome") — answers vote for outcome keys (answer tags = keys).
     Points are summed per key; the highest-scoring outcome wins (ties / no
     votes fall back to the FIRST outcome, the default). Dietary needs filter
     the winning outcome's products; if the hero fails a need it's swapped for
     the first qualifying stack product. The outcome's hand-written card shows.

   • TAGS ("tags", legacy) — hard dietary FILTER → tag SCORING on survivors →
     bestseller FALLBACK, ranked across the rendered product pool.

   Add ?quiz_debug=1 to the URL to log scoring to the console. */
(function () {
  'use strict';

  const root = document.querySelector('[data-quiz]');
  if (!root) return;

  const DEBUG = /[?&]quiz_debug=1\b/.test(window.location.search);
  const mode = root.dataset.mode === 'outcome' ? 'outcome' : 'tags';
  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  const screens = {
    intro: root.querySelector('[data-quiz-screen="intro"]'),
    results: root.querySelector('[data-quiz-screen="results"]')
  };
  const questions = [...root.querySelectorAll('[data-quiz-screen="question"]')];
  const total = questions.length;
  const answers = new Array(total).fill(null);
  let current = 0;

  function parseJSON(sel) {
    const el = root.querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  let products = parseJSON('[data-quiz-products]') || [];
  products.forEach((p) => { p.tags = (p.tags || []).map((t) => String(t).trim().toLowerCase()).filter(Boolean); });

  let outcomes = parseJSON('[data-quiz-outcomes]') || [];
  outcomes.forEach((o) => {
    o.key = String(o.key || '').trim().toLowerCase();
    if (o.hero) o.hero.tags = (o.hero.tags || []).map((t) => String(t).trim().toLowerCase());
    (o.stack || []).forEach((s) => { s.tags = (s.tags || []).map((t) => String(t).trim().toLowerCase()); });
  });

  /* legacy tag → [title, reason] for the "why this matches" cards */
  const TAG_LABEL = {
    energy: ['Boosts daily energy', 'matched to your energy goal'],
    gut: ['Supports your gut', 'you flagged digestion'],
    digestion: ['Eases digestion', 'for occasional bloating'],
    immunity: ['Immune support', 'matched to your goal'],
    recovery: ['Aids recovery', 'for performance & rest'],
    gym: ['Built for training', 'fits your gym routine'],
    morning: ['Morning-friendly', 'fits your AM routine'],
    afternoon: ['Beats the 3pm slump', 'targets your afternoon dip'],
    evening: ['Evening wind-down', 'for an evening crash'],
    smoothie: ['Blends into smoothies', 'fits your routine'],
    onthego: ['Grab-and-go format', 'for life on the move'],
    hydration: ['Hydration + electrolytes', 'for active days'],
    gentle: ['Gentle on your stomach', 'chosen for sensitivity']
  };

  function formatMoney(cents) {
    const value = cents / 100;
    const withCommas = (n, d) => { const p = n.toFixed(d).split('.'); p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); return d ? p.join('.') : p[0]; };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => {
      if (k === 'amount_no_decimals') return withCommas(value, 0);
      if (k === 'amount_with_comma_separator') return withCommas(value, 2).replace(/,/g, '§').replace(/\./, ',').replace(/§/g, '.');
      if (k === 'amount_no_decimals_with_comma_separator') return withCommas(value, 0).replace(/,/g, '.');
      return withCommas(value, 2);
    });
  }
  function escapeHtml(s) { return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

  /* ---- screen flow ---- */
  function show(name, index) {
    Object.values(screens).forEach((s) => s && s.classList.remove('is-active'));
    questions.forEach((q) => q.classList.remove('is-active'));
    if (name === 'question') {
      const q = questions[index];
      q.classList.add('is-active');
      const pct = Math.round(((index + 1) / total) * 100);
      q.querySelector('[data-quiz-step-label]').textContent = 'Question ' + (index + 1) + ' of ' + total;
      q.querySelector('[data-quiz-pct]').textContent = pct + '%';
      q.querySelector('[data-quiz-fill]').style.width = pct + '%';
    } else if (screens[name]) {
      screens[name].classList.add('is-active');
    }
  }

  root.querySelector('[data-quiz-start]')?.addEventListener('click', () => { current = 0; show('question', 0); });

  questions.forEach((q, idx) => {
    const next = q.querySelector('[data-quiz-next]');
    const back = q.querySelector('[data-quiz-back]');
    q.querySelectorAll('[data-quiz-answer]').forEach((btn) => {
      btn.addEventListener('click', () => {
        q.querySelectorAll('[data-quiz-answer]').forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        const tags = (btn.dataset.tags || '').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
        const need = (btn.dataset.need || '').trim().toLowerCase() || null;
        answers[idx] = { tags: tags, need: need, label: btn.dataset.label, weight: parseInt(q.dataset.weight, 10) || 1 };
        if (next) next.disabled = false;
      });
    });
    next?.addEventListener('click', () => {
      if (idx < total - 1) { current = idx + 1; show('question', current); }
      else { renderResults(); show('results'); }
    });
    back?.addEventListener('click', () => {
      if (idx > 0) { current = idx - 1; show('question', current); }
      else { show('intro'); }
    });
  });

  function activeNeeds() {
    return answers.filter(Boolean).map((a) => a.need).filter(Boolean);
  }
  function needLabelOf(needs) {
    if (!needs.length) return null;
    const a = answers.find((x) => x && x.need);
    return a ? a.label : null;
  }

  /* ---- outcome engine ---- */
  function pickOutcome() {
    const scores = {};
    outcomes.forEach((o) => { scores[o.key] = 0; });
    answers.forEach((a) => {
      if (!a || !a.tags) return;
      a.tags.forEach((key) => {
        if (key in scores) scores[key] += a.weight;
      });
    });
    let winner = outcomes[0];
    let best = -1;
    outcomes.forEach((o) => {
      if (scores[o.key] > best) { best = scores[o.key]; winner = o; }
    });
    if (DEBUG) console.log('[quiz] outcome scores', scores, '→', winner && winner.key);
    return { outcome: winner, scored: best > 0 };
  }

  function renderOutcome() {
    const { outcome, scored } = pickOutcome();
    if (!outcome) { screens.results.innerHTML = '<p>No matches found.</p>'; return; }

    const needs = activeNeeds();
    const passes = (p) => !p || needs.every((n) => (p.tags || []).includes(n));

    let hero = outcome.hero;
    let stack = (outcome.stack || []).slice();
    let softened = false;
    if (needs.length) {
      // Apply the dietary filter; if hero fails, promote the first qualifying stack item.
      let fHero = hero && passes(hero) ? hero : null;
      let fStack = stack.filter((s) => passes(s));
      if (!fHero && fStack.length) { fHero = fStack.shift(); }
      if (fHero || fStack.length) {
        hero = fHero; stack = fStack;
      } else {
        // Filter would empty this outcome — ignore it and show the curated picks with a note.
        softened = true;
        if (DEBUG) console.log('[quiz] dietary filter emptied outcome', outcome.key, '— softening');
      }
    }

    const reasons = (outcome.reasons || []).filter(Boolean).slice(0, 3);
    const nLabel = needLabelOf(needs);
    const filterBadge = needs.length
      ? (softened
          ? '<span class="badge badge--sub">Couldn’t match “' + escapeHtml(nLabel) + '” here — showing your closest match</span>'
          : '<span class="badge badge--sub">Filtered to: ' + escapeHtml(nLabel) + '</span>')
      : '';

    const whyHtml = reasons.length
      ? '<div class="quiz__why">' + reasons.map((r) => '<div class="quiz__why-card"><b><span>✓</span>' + escapeHtml(r) + '</b></div>').join('') + '</div>'
      : '';

    const heroHtml = hero
      ? '<div class="card quiz__hero">' + mediaHtml(hero, 'quiz__hero-media') +
          '<div class="quiz__hero-body">' +
            '<span class="badge badge--sub" style="align-self:flex-start">' + escapeHtml(hero.cat || 'Best match') + '</span>' +
            '<a class="serif quiz__hero-title" href="' + hero.url + '">' + escapeHtml(hero.title) + '</a>' +
            '<div class="quiz__hero-price">' + priceHtml(hero) + '</div>' +
            '<div class="quiz__hero-actions">' +
              '<button type="button" class="btn btn--primary" data-quiz-add="' + hero.id + '">Add to cart →</button>' +
              '<a class="btn btn--outline" href="' + hero.url + '">View product</a>' +
            '</div>' +
          '</div>' +
        '</div>'
      : '';

    const stackHtml = stack.length
      ? '<div class="quiz__stack"><div class="quiz__stack-head">Complete your stack</div>' +
        stack.map((s) => '<div class="quiz__stack-row">' + mediaHtml(s, 'quiz__stack-media') +
          '<div class="quiz__stack-info"><b>' + escapeHtml(s.title) + '</b><small>' + escapeHtml(s.cat || '') + ' · ' + formatMoney(s.price) + '</small></div>' +
          '<button type="button" class="btn btn--outline btn--sm" data-quiz-add="' + s.id + '">Add</button></div>').join('') +
        '</div>'
      : '';

    screens.results.innerHTML =
      '<div class="quiz__results-head">' +
        '<span class="quiz__results-kicker">Your result</span>' +
        '<h2 class="quiz__results-title">' + escapeHtml(outcome.title) + '</h2>' +
        (outcome.subtitle ? '<p class="quiz__results-sub">' + escapeHtml(outcome.subtitle) + '</p>' : '') +
        filterBadge +
      '</div>' + heroHtml + whyHtml + stackHtml +
      '<div class="quiz__restart"><button type="button" data-quiz-restart>↻ Retake quiz</button></div>';

    bindResults();
  }

  /* ---- legacy tag engine ---- */
  function recommend() {
    const needs = activeNeeds();
    let pool = products.filter((p) => needs.every((n) => p.tags.includes(n)));
    if (pool.length === 0) pool = products.slice();

    const scored = pool.map((p) => {
      let score = 0; const hits = [];
      answers.forEach((a) => {
        if (!a || !a.tags || !a.tags.length) return;
        a.tags.forEach((t) => { if (p.tags.includes(t)) { score += a.weight; if (hits.indexOf(t) === -1) hits.push(t); } });
      });
      return { p: p, score: score, hits: hits };
    }).sort((x, y) => y.score - x.score);

    const strong = scored.filter((s) => s.score > 0);
    const fallback = strong.length === 0;
    const ordered = fallback
      ? scored.filter((s) => s.p.best).concat(scored.filter((s) => !s.p.best))
      : strong;
    return { picks: ordered.slice(0, 3), fallback: fallback, needLabel: needLabelOf(needs) };
  }

  function renderTags() {
    const { picks, fallback, needLabel } = recommend();
    if (!picks.length) { screens.results.innerHTML = '<p>No matches found.</p>'; return; }
    const hero = picks[0].p;
    const heroHits = picks[0].hits.length ? picks[0].hits : hero.tags;
    const why = heroHits.map((t) => TAG_LABEL[t]).filter(Boolean).slice(0, 3);
    const rest = picks.slice(1);

    const whyHtml = why.length
      ? '<div class="quiz__why">' + why.map((w) => '<div class="quiz__why-card"><b><span>✓</span>' + escapeHtml(w[0]) + '</b><small>' + escapeHtml(w[1]) + '</small></div>').join('') + '</div>'
      : '';
    const stackHtml = rest.length
      ? '<div class="quiz__stack"><div class="quiz__stack-head">Complete your stack</div>' +
        rest.map((s) => '<div class="quiz__stack-row">' + mediaHtml(s.p, 'quiz__stack-media') +
          '<div class="quiz__stack-info"><b>' + escapeHtml(s.p.title) + '</b><small>' + escapeHtml(s.p.cat || '') + ' · ' + formatMoney(s.p.price) + '</small></div>' +
          '<button type="button" class="btn btn--outline btn--sm" data-quiz-add="' + s.p.id + '">Add</button></div>').join('') +
        '</div>'
      : '';

    screens.results.innerHTML =
      '<div class="quiz__results-head">' +
        '<span class="quiz__results-kicker">Your result</span>' +
        '<h2 class="quiz__results-title">Your perfect match</h2>' +
        '<p class="quiz__results-sub">' + (fallback ? 'A great place to start — our most-loved daily essentials.' : 'Matched to your goals across the range.') + '</p>' +
        (needLabel ? '<span class="badge badge--sub">Filtered to: ' + escapeHtml(needLabel) + '</span>' : '') +
      '</div>' +
      '<div class="card quiz__hero">' + mediaHtml(hero, 'quiz__hero-media') +
        '<div class="quiz__hero-body">' +
          '<span class="badge badge--sub" style="align-self:flex-start">' + escapeHtml(hero.cat || 'Best match') + '</span>' +
          '<a class="serif quiz__hero-title" href="' + hero.url + '">' + escapeHtml(hero.title) + '</a>' +
          '<div class="quiz__hero-price">' + priceHtml(hero) + '</div>' +
          '<div class="quiz__hero-actions">' +
            '<button type="button" class="btn btn--primary" data-quiz-add="' + hero.id + '">Add to cart →</button>' +
            '<a class="btn btn--outline" href="' + hero.url + '">View product</a>' +
          '</div>' +
        '</div>' +
      '</div>' + whyHtml + stackHtml +
      '<div class="quiz__restart"><button type="button" data-quiz-restart>↻ Retake quiz</button></div>';

    bindResults();
  }

  function renderResults() {
    if (mode === 'outcome' && outcomes.length) renderOutcome();
    else renderTags();
  }

  /* ---- shared render helpers ---- */
  function priceHtml(p) {
    let html = '<span class="serif tnum">' + formatMoney(p.price) + '</span>';
    if (p.compare && p.compare > p.price) {
      const pct = Math.round((p.compare - p.price) / p.compare * 100);
      html += '<s class="tnum" style="color:var(--text-faint)">' + formatMoney(p.compare) + '</s>';
      html += '<span class="badge badge--sale">Save ' + pct + '%</span>';
    }
    return html;
  }

  function mediaHtml(p, cls) {
    if (p.image) return '<span class="media media--square ' + cls + '"><img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" loading="lazy"></span>';
    return '<span class="media media--square media--placeholder ' + cls + '"></span>';
  }

  function bindResults() {
    screens.results.querySelectorAll('[data-quiz-add]').forEach((btn) => {
      btn.addEventListener('click', () => addToCart(btn, parseInt(btn.dataset.quizAdd, 10)));
    });
    screens.results.querySelector('[data-quiz-restart]')?.addEventListener('click', () => {
      answers.fill(null);
      questions.forEach((q) => { q.querySelectorAll('[data-quiz-answer]').forEach((b) => b.classList.remove('is-selected')); q.querySelector('[data-quiz-next]').disabled = true; });
      current = 0; show('intro');
    });
  }

  async function addToCart(btn, id) {
    if (!id) return;
    const G = (window.theme && window.theme.strings) || {};
    const label = btn.textContent;
    btn.disabled = true; btn.textContent = G.adding || 'Adding…';
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: id, quantity: 1 }) });
      if (!res.ok) throw new Error('add failed');
      window.location.href = '/cart';
    } catch (e) {
      btn.disabled = false; btn.textContent = G.try_again || 'Try again';
      setTimeout(() => { btn.textContent = label; }, 1200);
    }
  }
})();
